import { Encodings } from './encodings';
import { Queries } from './queries';
import { Schemas } from './schemas';
import { UniqueKeys } from './uniqueKeys';
import { IncentivesDSMetadatas } from './incentivesDSMetadatas';
// Collection of all metadata for a tool.
// Features:
// - exported names on all metadata (which align to port names)
// - strongly typed metadata
// - serializable
// - intended to be extensible to new types without breaking existing code
// - immutable by design: use ToolScopeBuilder to construct and modify existing scopes convienently, building
// a ToolScope in other ways is likely to break as things change.
// - ToolScope.empty available for a preconstructed empty scope as needed.
export class ToolScope {
    constructor(user, data, schemas, samples, encodings, uniqueKeys, pipeID, dependants, dependee, dependeeMeta, reusableLogicID, reusableLogicDependants, reusableLogic, reusableLogicMeta, incentivesDSMetadatas) {
        this.user = user;
        this.data = data;
        this.schemas = schemas;
        this.samples = samples;
        this.encodings = encodings;
        this.pipeID = pipeID;
        this.dependants = dependants;
        this.dependee = dependee;
        this.dependeeMeta = dependeeMeta;
        this.uniqueKeys = uniqueKeys;
        this.reusableLogicID = reusableLogicID;
        this.reusableLogicDependants = reusableLogicDependants;
        this.reusableLogic = reusableLogic;
        this.reusableLogicMeta = reusableLogicMeta;
        this.incentivesDSMetadatas = incentivesDSMetadatas;
    }
    get names() {
        const output = new Set([
            ...this.data.names,
            ...this.schemas.names,
            ...this.samples.names,
            ...this.encodings.names,
            ...this.uniqueKeys.names,
            ...this.incentivesDSMetadatas.names
        ]);
        return output;
    }
    static merge(...scopes) {
        if (scopes.length === 0)
            return ToolScope.empty;
        const user = scopes.map((s) => s.user).reduce((prev, current) => current ?? prev, undefined);
        const data = Schemas.merge(...scopes.map((s) => s.data));
        const schemas = Schemas.merge(...scopes.map((s) => s.schemas));
        const samples = Queries.merge(...scopes.map((s) => s.samples));
        const encodings = Encodings.merge(...scopes.map((s) => s.encodings));
        const uniqueKeys = UniqueKeys.merge(...scopes.map((s) => s.uniqueKeys));
        const incentivesDSMetadatas = IncentivesDSMetadatas.merge(...scopes.map((s) => s.incentivesDSMetadatas));
        // takes first defined pipeID
        const pipeID = scopes.map((s) => s.pipeID).reduce((prev, current) => prev ?? current, undefined);
        const reusableLogicID = scopes.map((s) => s.reusableLogicID).reduce((prev, current) => prev ?? current, undefined);
        // takes longest chain of dependants, ignores invalid state where chain contents differ
        const dependants = scopes.map((s) => s.dependants).reduce((p, c) => (c.length > p.length ? c : p), []);
        const reusableLogicDependants = scopes
            .map((s) => s.reusableLogicDependants)
            .reduce((p, c) => (c.length > p.length ? c : p), []);
        const dependee = undefined;
        const dependeeMeta = undefined;
        const reusableLogic = undefined;
        const reusableLogicMeta = undefined;
        return new ToolScope(user, data, schemas, samples, encodings, uniqueKeys, pipeID, dependants, dependee, dependeeMeta, reusableLogicID, reusableLogicDependants, reusableLogic, reusableLogicMeta, incentivesDSMetadatas);
    }
    isEmpty() {
        return this.data.size === 0 && this.schemas.size === 0 && this.samples.size === 0 && this.encodings.size === 0;
    }
}
(function (ToolScope) {
    ToolScope.empty = new ToolScope(undefined, Schemas.empty, Schemas.empty, Queries.empty, Encodings.empty, UniqueKeys.empty, undefined, [], undefined, undefined, undefined, [], undefined, undefined, IncentivesDSMetadatas.empty);
})(ToolScope || (ToolScope = {}));
//# sourceMappingURL=toolScope.js.map